import React, { useCallback, useContext, useEffect, useState } from "react";

import { useSnackbar } from "notistack";
import { useHistory, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import Header from "../../../../components/Header";
import Loading from "../../../../components/Loading";

import Button from "../../../../componentsv2/Button";
import Container from "../../../../componentsv2/Container";
import Box from "../../../../componentsv2/Box";

import formatarData from "../../../../utils/formatarData";
import {
  OPCOES_LOCAL_ATENDIMENTO,
  OPCOES_PAGAMENTO_STATUS,
  WHATSAPP_AGENDA_CARE_URL,
} from "../../constants/agendamentoOptions";
import { abrirNoMaps, temEnderecoMinimo } from "../../utils/abrirNoMaps";
import {
  arredondarMoeda,
  calcularSubtotal,
  formatarMoedaBrl,
  parseDesconto,
} from "../../utils/descontoAgendamento";
import { formatarNomeEnfermeiro } from "../../utils/evolucaoFormatters";
import ProgressoSessoes from "../../Components/ProgressoSessoes";
import ModalPreviewPrescricao from "../../Components/ModalPreviewPrescricao";
import BotaoPdfAgendamento from "../../Components/BotaoPdfAgendamento";
import usePreviewPrescricao from "../../data/hooks/usePreviewPrescricao";
import {
  baixarBlob,
  baixarBlobUrl,
  buscarBlobPrescricao,
} from "../../utils/prescricaoArquivos";

import { Context } from "../../../../Context/PermissoesContext";
import { api } from "../../../../services/Api";

import SecaoCard from "./components/SecaoCard";
import CampoInfo from "./components/CampoInfo";
import BadgeSituacaoAgendamento from "../../Components/BadgeSituacaoAgendamento";
import PrescricoesVisualizar from "./components/PrescricoesVisualizar";

function labelOpcao(opcoes, valor) {
  return opcoes.find(opcao => opcao.value === valor)?.label || valor || "—";
}

export default function VisualizarAgendamento() {
  const { enqueueSnackbar } = useSnackbar();
  const { checarPermissao } = useContext(Context);
  const [situacao, setSituacao] = useState("");
  const [loading, setLoading] = useState(false);
  const [cliente, setCliente] = useState({
    id: "",
    nome: "",
    cpf: "",
    celular: "",
  });
  const [endereco, setEndereco] = useState("");
  const [cep, setCep] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [complemento, setComplemento] = useState("");
  const [observacao, setObservacao] = useState("");
  const [observacaoCliente, setObservacaoCliente] = useState("");
  const [data, setData] = useState("");
  const [hora, setHora] = useState("");
  const [numeroEndereco, setNumeroEndereco] = useState("");
  const [estado, setEstado] = useState("");
  const [desconto, setDesconto] = useState(0);
  const [totalComDesconto, setTotalComDesconto] = useState(0);
  const [formaPagamento, setFormaPagamento] = useState("");
  const [localAtendimento, setLocalAtendimento] = useState("");
  const [pagamentoStatus, setPagamentoStatus] = useState("");
  const [prescricoes, setPrescricoes] = useState([]);
  const [loadingPrescricoes, setLoadingPrescricoes] = useState(false);
  const [agendamentoId, setAgendamentoId] = useState("");
  const [pdfVersaoCache, setPdfVersaoCache] = useState(0);
  const [enfermeiroId, setEnfermeiroId] = useState("");
  const [enfermeiroNome, setEnfermeiroNome] = useState("");

  const history = useHistory();
  const location = useLocation();
  const previewPrescricao = usePreviewPrescricao();

  const carregarPrescricoes = useCallback(
    async (id) => {
      if (!id) return;

      try {
        setLoadingPrescricoes(true);
        const {
          data: { data },
        } = await api.get(`/v2/agendaCare/agendamento/${id}/prescricoes`);
        setPrescricoes(Array.isArray(data) ? data : []);
      } catch (error) {
        enqueueSnackbar(error.response?.data?.messages, {
          variant: "warning",
        });
      } finally {
        setLoadingPrescricoes(false);
      }
    },
    [enqueueSnackbar]
  );

  const carregarObservacaoCliente = useCallback(
    async (clienteId) => {
      if (!clienteId) {
        setObservacaoCliente("");
        return;
      }

      try {
        const {
          data: { data },
        } = await api.get(`/v2/agendaCare/cliente/${clienteId}/observacoes`);
        const observacoes = Array.isArray(data) ? data : [];
        setObservacaoCliente(observacoes[0]?.descricao || "");
      } catch {
        setObservacaoCliente("");
      }
    },
    []
  );

  const handleDownloadPrescricao = async (prescricao) => {
    if (!location.state?.row?.id || !prescricao?.id) return;

    try {
      const blob = await buscarBlobPrescricao(location.state.row.id, prescricao);
      baixarBlob(blob, prescricao.nomeOriginal);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.messages || "Não foi possível baixar a prescrição.", {
        variant: "error",
      });
    }
  };

  const handleVisualizarPrescricao = async (prescricao) => {
    if (!location.state?.row?.id || !prescricao?.id) return;

    try {
      await previewPrescricao.abrirPreviewComBlob(prescricao, () =>
        buscarBlobPrescricao(location.state.row.id, prescricao)
      );
    } catch (error) {
      enqueueSnackbar(error.response?.data?.messages || "Não foi possível visualizar a prescrição.", {
        variant: "error",
      });
    }
  };

  const handleDownloadPreview = () => {
    if (!previewPrescricao.blobUrl || !previewPrescricao.arquivoPreview) return;

    baixarBlobUrl(
      previewPrescricao.blobUrl,
      previewPrescricao.arquivoPreview.nomeOriginal
    );
  };

  const calculaDesconto = (procedimentos, descontoValor) => {
    const itens = procedimentos.map(el => ({
      precoExame: el.agendamento_procedimento?.precoAplicado ?? el.precoExame,
      quantidade: el.agendamento_procedimento?.quantidade ?? 1,
    }));
    const somatoria = calcularSubtotal(itens);
    const descontoNum = parseDesconto(descontoValor) || 0;
    setDesconto(descontoNum);
    setTotalComDesconto(arredondarMoeda(somatoria - descontoNum));
  };

  const pegarDadosAgendamento = useCallback(async () => {
    setLoading(true);

    if (location.state?.row) {
      try {
        const {
          data: { data },
        } = await api.get(`v2/agendaCare/agendamento/${location.state.row.id}`);

        setAgendamentoId(location.state.row.id);
        setEnfermeiroId(data.enfermeiro?.id || "");
        setEnfermeiroNome(formatarNomeEnfermeiro(data.enfermeiro));
        setData(formatarData(new Date(data.data), false));
        setHora(data.hora);
        setSituacao(data.situacao);
        setCliente(data.cliente);
        setEndereco(data.endereco);
        setCep(data.cep);
        setBairro(data.bairro);
        setCidade(data.cidade);
        setComplemento(data.complemento || data.cliente?.complemento || "");
        setObservacao(data.observacoes);
        setNumeroEndereco(data.enderecoNumero);
        setFormaPagamento(data.formaPagamento);
        setLocalAtendimento(data.localAtendimento);
        setPagamentoStatus(data.pagamentoStatus);
        setEstado(data.estado);
        calculaDesconto(data.procedimentos, data.desconto);
        setPdfVersaoCache(versao => versao + 1);
        await carregarPrescricoes(location.state.row.id);
        await carregarObservacaoCliente(data.cliente?.id);
      } catch (error) {
        enqueueSnackbar(error.response?.data?.messages, {
          variant: "error",
        });
      }
    } else {
      history.push("/agendaCare");
    }
    setLoading(false);
  }, [carregarObservacaoCliente, carregarPrescricoes, enqueueSnackbar, history, location.state?.row]);

  useEffect(() => {
    pegarDadosAgendamento();
  }, [pegarDadosAgendamento]);

  const enderecoCompleto = [endereco, numeroEndereco].filter(Boolean).join(", ");
  const cidadeEstado = [cidade, estado].filter(Boolean).join(" — ");

  return (
    <>
      <Header titulo="Visualizar Agendamentos" />
      <Container maxWidth={false} sx={{ px: { xs: 3, md: 6, lg: 8 }, pt: 3, pb: 5, bgcolor: "#F5F5F5" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button onClick={() => history.push({ pathname: "/agendaCare" })}>
            <ArrowLeft size={22} color="#003399" strokeWidth={2} />
          </Button>

          <BotaoPdfAgendamento
            agendamentoId={agendamentoId}
            versaoCache={pdfVersaoCache}
            label="Visualizar PDF"
            disabled={!agendamentoId}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 2,
            maxWidth: 960,
            mx: "auto",
            width: "100%",
          }}
        >
          <SecaoCard titulo="Informações do Cliente">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                columnGap: 3,
                rowGap: 2.5,
                width: "100%",
              }}
            >
              <CampoInfo semGrid label="Nome" valor={cliente.nome || "—"} />
              <CampoInfo semGrid label="CPF" valor={cliente.cpf || "—"} />
              <CampoInfo
                semGrid
                label="Telefone"
                valor={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <span>{cliente.celular || "—"}</span>
                    <Button
                      component="a"
                      href={WHATSAPP_AGENDA_CARE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      type="button"
                      size="small"
                      variant="text"
                      style={{ padding: 0, minWidth: "auto" }}
                    >
                      WhatsApp
                    </Button>
                  </Box>
                }
              />

              <CampoInfo semGrid label="Endereço" valor={enderecoCompleto || "—"} />
              <CampoInfo semGrid label="Bairro" valor={bairro || "—"} />
              <CampoInfo semGrid label="Cidade" valor={cidadeEstado || "—"} />

              <CampoInfo semGrid label="CEP" valor={cep || "—"} />
              <CampoInfo semGrid label="Complemento" valor={complemento || "—"} />
              <Box sx={{ display: "flex", alignItems: "flex-end", minWidth: 0 }}>
                <Button
                  type="button"
                  size="small"
                  variant="text"
                  style={{ padding: 0, minWidth: "auto" }}
                  disabled={!temEnderecoMinimo({ endereco, cidade, cep })}
                  onClick={() =>
                    abrirNoMaps({
                      endereco,
                      numero: numeroEndereco,
                      bairro,
                      cidade,
                      estado,
                      cep,
                    })
                  }
                >
                  Abrir no Maps
                </Button>
              </Box>

              <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" }, pt: 1 }}>
                <CampoInfo
                  semGrid
                  bloco
                  italico={Boolean(observacaoCliente)}
                  label="Observações do cliente"
                  valor={observacaoCliente || "Nenhuma observação registrada."}
                />
              </Box>
            </Box>
          </SecaoCard>

          <SecaoCard titulo="Informações do Pedido">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                columnGap: 3,
                rowGap: 2.5,
                width: "100%",
              }}
            >
              <CampoInfo
                semGrid
                label="Situação"
                valor={<BadgeSituacaoAgendamento situacao={situacao} />}
              />
              <CampoInfo semGrid label="Data" valor={data || "—"} />
              <CampoInfo semGrid label="Horário" valor={hora || "—"} />
              <CampoInfo semGrid label="Enfermeiro" valor={enfermeiroNome} />
              <CampoInfo
                semGrid
                label="Local"
                valor={labelOpcao(OPCOES_LOCAL_ATENDIMENTO, localAtendimento)}
              />
              <CampoInfo
                semGrid
                label="Pagamento"
                valor={labelOpcao(OPCOES_PAGAMENTO_STATUS, pagamentoStatus)}
              />
              <CampoInfo semGrid label="Forma pagamento" valor={formaPagamento || "—"} />
              {checarPermissao("AGC002") && (
                <>
                  <CampoInfo semGrid label="Desconto" valor={formatarMoedaBrl(desconto)} />
                  <CampoInfo semGrid label="Total" valor={formatarMoedaBrl(totalComDesconto)} />
                </>
              )}
              <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" }, pt: observacao ? 1 : 0 }}>
                <CampoInfo semGrid bloco label="Observações do pedido" valor={observacao || "—"} />
              </Box>
            </Box>
          </SecaoCard>

          <SecaoCard titulo="Progresso e Sessões">
            <ProgressoSessoes
              agendamentoId={agendamentoId}
              enfermeiroIdDefault={enfermeiroId}
              enfermeiroNomeDefault={enfermeiroNome}
              localAtendimento={localAtendimento}
              readOnly
              layoutColunas
              semTitulo
            />
          </SecaoCard>

          <SecaoCard titulo="Prescrição Médica">
            <PrescricoesVisualizar
              prescricoes={prescricoes}
              loading={loadingPrescricoes}
              onPreview={handleVisualizarPrescricao}
              onDownload={handleDownloadPrescricao}
            />
          </SecaoCard>
        </Box>
      </Container>
      <ModalPreviewPrescricao
        open={previewPrescricao.open}
        loading={previewPrescricao.loading}
        arquivo={previewPrescricao.arquivoPreview}
        blobUrl={previewPrescricao.blobUrl}
        onClose={previewPrescricao.fecharPreview}
        onDownload={handleDownloadPreview}
      />
      {loading && <Loading />}
    </>
  );
}
